import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCurrency } from '../context/CurrencyContext';
import { FaTrash, FaBell, FaPlus } from 'react-icons/fa';

const AlertsManagement = () => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { formatPrice } = useCurrency();

    // Form State
    const [currency, setCurrency] = useState('USD');
    const [targetPrice, setTargetPrice] = useState('');
    const [condition, setCondition] = useState('ABOVE');

    const currencies = [
        { code: 'USD', name: 'Amerikan Doları' },
        { code: 'EUR', name: 'Euro' },
        { code: 'GBP', name: 'İngiliz Sterlini' },
        { code: 'GA', name: 'Gram Altın' },
        { code: 'C', name: 'Çeyrek Altın' },
        { code: 'Y', name: 'Yarım Altın' },
        { code: 'T', name: 'Tam Altın' },
        { code: 'XAG', name: 'Gümüş (Ons)' }
    ];

    useEffect(() => {
        fetchAlerts();
    }, []);

    const fetchAlerts = async () => {
        try {
            const { data } = await axios.get('/api/price-alerts');
            setAlerts(data);
            setLoading(false);
        } catch (error) {
            console.error('Alarmlar yüklenemedi:', error);
            setLoading(false);
        }
    };

    const handleCreateAlert = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/price-alerts', {
                currency,
                targetPrice: parseFloat(targetPrice),
                condition
            });
            setTargetPrice('');
            fetchAlerts();
        } catch (error) {
            alert('Alarm oluşturulamadı: ' + error.response?.data?.message || error.message);
        }
    };

    const handleDeleteAlert = async (id) => {
        if (!window.confirm('Alarmı silmek istediğinize emin misiniz?')) return;
        try {
            await axios.delete(`/api/price-alerts/${id}`);
            fetchAlerts();
        } catch (error) {
            console.error('Silme hatası:', error);
        }
    };

    return (
        <div className="w-full min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-6 text-gray-900 dark:text-white">
                <FaBell className="text-yellow-500 text-xl" />
                <h2 className="text-xl font-bold">Fiyat Alarmları</h2>
            </div>

            {/* Create Alert Form */}
            <form onSubmit={handleCreateAlert} className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg mb-8 border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Varlık</label>
                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="w-full p-2 rounded border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        >
                            {currencies.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Koşul</label>
                        <select
                            value={condition}
                            onChange={(e) => setCondition(e.target.value)}
                            className="w-full p-2 rounded border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        >
                            <option value="ABOVE">Büyüktür (&gt;)</option>
                            <option value="BELOW">Küçüktür (&lt;)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Hedef Fiyat (TL)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={targetPrice}
                            onChange={(e) => setTargetPrice(e.target.value)}
                            className="w-full p-2 rounded border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="0.00"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className="bg-yellow-500 hover:bg-yellow-600 text-white p-2 rounded flex items-center justify-center gap-2 transition"
                    >
                        <FaPlus /> Alarm Ekle
                    </button>
                </div>
            </form>

            {/* Alerts List */}
            <div className="space-y-4">
                {loading ? (
                    <div className="text-center py-4 text-gray-500">Yükleniyor...</div>
                ) : alerts.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400 italic">Henüz kurulu alarmınız yok.</div>
                ) : (
                    alerts.map(alert => (
                        <div key={alert._id} className={`flex justify-between items-center p-4 rounded-lg border ${alert.isTriggered ? 'bg-red-50 dark:bg-red-900/20 border-red-200' : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 shadow-sm'}`}>
                            <div>
                                <div className="font-bold text-gray-800 dark:text-white">
                                    {alert.currencyName || alert.currency}
                                    <span className="mx-2 text-gray-400 text-sm">şu an: {formatPrice(alert.currentPrice)}</span>
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                                    <span>Hedef: <strong>{formatPrice(alert.targetPrice)}</strong></span>
                                    <span className="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">
                                        {alert.condition === 'ABOVE' ? 'Üzerine çıkarsa' : 'Altına düşerse'}
                                    </span>
                                </div>
                                {alert.isTriggered && (
                                    <div className="text-xs text-red-500 mt-1 font-semibold">
                                        ⚠️ Tetiklendi: {new Date(alert.triggeredAt).toLocaleString('tr-TR')}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => handleDeleteAlert(alert._id)}
                                className="text-red-400 hover:text-red-600 bg-red-50 dark:bg-red-900/30 p-2 rounded-full transition"
                            >
                                <FaTrash />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default AlertsManagement;
